import Stripe from "stripe";
import { getTotalMonthsInBillingPeriod } from "../utils/subscription";

export const convertStripeDecimalPriceToPrice = (
  price: number,
  includeDecimals?: true
) => {
  return Number(
    (Math.round(price * 100) / 10000).toFixed(includeDecimals ? 1 : 0)
  );
};

export function convertStripePriceToMonthlyPrice(
  priceDecimal: number,
  billingPeriod: string
) {
  const totalMonths = getTotalMonthsInBillingPeriod(billingPeriod);
  return Math.round(
    convertStripeDecimalPriceToPrice(priceDecimal / totalMonths, true)
  );
}

export function prettyDate(
  date: Date | number | string | undefined, // unix code or date object or iso string
  shorthand?: true,
  shortYear?: true,
  withTime?: true
) {
  if (!date) return "N/A";
  const dateObj =
    typeof date === "string" || typeof date === "number"
      ? new Date(date)
      : date;
  return dateObj.toLocaleString("en-GB", {
    hour: withTime ? "2-digit" : undefined,
    minute: withTime ? "2-digit" : undefined,
    weekday: shorthand ? undefined : "long",
    day: "numeric",
    month: shorthand ? "2-digit" : "long",
    year: shortYear ? "2-digit" : "numeric",
  });
}

export function prettyTime(unixCode: number) {
  const date = new Date(unixCode);
  return date.toLocaleString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function convertSecondsToMinutes(seconds: number) {
  return Math.floor(seconds / 60);
}

export function convertSecondsToHoursMinsSecString(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const remainingSeconds = seconds % 60;
  if (seconds < 60) {
    return `${seconds} seconds`;
  } else if (minutes < 60) {
    return `${minutes} mins${
      remainingSeconds > 0 ? ` ${remainingSeconds}s` : ""
    }`;
  } else {
    return `${hours} h${
      remainingMinutes > 0 ? ` ${remainingMinutes} mins` : ""
    }`;
  }
}

export function convertSecondsToHoursMinsString(
  seconds: number,
  withoutMinuteText?: true
) {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (minutes < 60) {
    return `${minutes} ${withoutMinuteText ? "" : "mins"}`;
  } else {
    return `${hours} h${
      remainingMinutes > 0 ? ` ${remainingMinutes} mins` : ""
    }`;
  }
}

export function isoStringToUnix(dateString: string) {
  const inputDate = new Date(dateString);
  return Math.floor(inputDate.getTime());
}

export function prettyCurrency(currencyCode: string) {
  return currencyCode === "usd" ? "$" : currencyCode === "eur" ? "€" : "£";
}

export function prettyPrice(
  price: number,
  currency: string,
  cleanDecimals?: true
) {
  return `${prettyCurrency(currency)}${
    cleanDecimals ? Number(price.toFixed(2)) : price
  }`;
}

export function convertStripeIntervalToPrettyDuration(duration: string) {
  const interval = Number(duration) < 12 ? "month" : "year";
  const intervalCount =
    duration === "12" ? 1 : duration === "24" ? 2 : Number(duration);
  const prettyDuration =
    intervalCount === 1
      ? `${intervalCount} ${interval}`
      : `${intervalCount} ${interval}s`;
  return { interval, intervalCount, prettyDuration };
}

export function convertPrettyDurationToStripeInterval(prettyDuration: string) {
  const intervalCount = Number(prettyDuration.split(" ")[0]);
  const interval = prettyDuration.split(" ")[1];
  return {
    interval: interval === "months" ? "month" : interval,
    intervalCount,
  };
}

export function convertStripeIntervalToMonths(
  interval: string,
  intervalCount: number
) {
  return intervalCount * (interval === "month" ? 1 : 12);
}

/**
 * Maps Stripe subscription status to our enum values.
 * @param {Stripe.Subscription.Status} status - The stripe subscription status
 * @returns {"active" | "expired" | "trialing" | "suspended"} The mapped status
 */
// DIRECT DUPLICATE OF B/E FUNCTION - DO NOT CHANGE!
export function mapStripeStatus(status: Stripe.Subscription.Status) {
  switch (status) {
    case "active":
      return "active";
    case "canceled":
      return "expired";
    case "trialing":
      return "trialing";
    default:
      return "suspended";
  }
}

export function getFriendlyInvoiceStatus(status: Stripe.Invoice.Status | null) {
  switch (status) {
    case "paid":
      return "Paid";
    case "open":
      return "Pending";
    default:
      return "Failed";
  }
}
